import type { Distance, SpatialRectangle } from "@pdfking/math"
import { Min, Scale, Subtract, Total } from "@pdfking/math"
import { ZERO_DISTANCE } from "@pdfking/math/constants"
import type { PageShape } from "@pdfking/renderer-api"
import { HorizontalAlign, VerticalAlign } from "@pdfking/renderer-api"
import { Range } from "@pdfking/tfunc"

import { CAP_HEIGHT } from "#constants"

import { PlotLine } from "../layout/PlotLine"
import { PlainText } from "../text/PlainText"
import { DayColor } from "./DayColor"
import { WeekdayFont } from "./WeekdayFont"
import { WeekdayNames } from "./WeekdayNames"

/** Days `first` to `last` of month `month` of `year` down `box`, a day a
 * line of `rows` as a tall calendar has them: its number, its weekday after
 * it, ruled under; Sundays holidays (see DayColor). Days past the month's last are
 * none. `aligned`, each is on its weekday's row from the Monday of the week
 * of `first`, its number alone across the box, as a year's columns are. Its
 * weekdays in `language`. */
export const DrawDayList = ({
  aligned,
  box,
  first,
  language,
  last,
  month,
  rows,
  year,
}: {
  aligned: boolean
  box: SpatialRectangle<Distance>
  first: number
  language: string
  last: number
  month: number
  rows: number
  year: number
}): PageShape[] => {
  const days = Min(last, new Date(Date.UTC(year, month, 0)).getUTCDate())
  const row = Scale(box.height, 1 / rows)
  // As tall as the row lets it, and narrow enough for a number and its
  // weekday across the box
  const font = Min(Scale(row, 0.6), Scale(box.width, aligned ? 0.35 : 0.25))
  const names = WeekdayNames(language)
  const top = Total(box.y, box.height)
  const weekday_of = (day: number) => (new Date(Date.UTC(year, month - 1, day)).getUTCDay() + 6) % 7
  const skip = aligned ? weekday_of(first) : 0
  return [...Range(first, days)].flatMap(day => {
    const weekday = weekday_of(day)
    const color = DayColor(weekday)
    const y = Subtract(top, Scale(row, skip + day - first + 1))
    const text_y = Total(y, Scale(Subtract(row, font), 0.5))
    if (aligned)
      return [PlainText(`${day}`, box.x, box.width, text_y, font, color, HorizontalAlign.CENTER)]
    // The number and its weekday on one baseline, the number's figures
    // centred in the row
    const baseline = Total(y, Scale(Subtract(row, Scale(font, CAP_HEIGHT)), 0.5))
    return [
      {
        ...PlainText(`${day}`, box.x, Scale(font, 2), baseline, font, color),
        vertical_align: VerticalAlign.BOTTOM,
      },
      {
        ...PlainText(
          names[weekday]!,
          Total(box.x, Scale(font, 2)),
          Subtract(box.width, Scale(font, 2)),
          baseline,
          WeekdayFont(font),
          color
        ),
        vertical_align: VerticalAlign.BOTTOM,
      },
      PlotLine(box.x, y, box.width, ZERO_DISTANCE),
    ]
  })
}
